import React, { useState, useEffect } from "react";
import { Menu, MenuItem, Checkbox, FormControlLabel, Button, TextField, Tooltip } from "@material-ui/core";
import { FaChevronRight as ChevronRightIcon, FaChevronDown as ArrowDropDownIcon } from "react-icons/fa";

const DropdownWithNestedMenu = ({ title, icon, categories, onApply, onInitialLoad, defaultSelectAll = false, defaultSelectedOptions, compact = false }) => {
    // Initialize each category's selection based on defaultSelectAll and defaultSelectedOptions

    const initialSelectedOptions = Object.keys(categories).reduce((acc, category) => {
        if (defaultSelectAll) {
            // If defaultSelectAll is true, select all values
            acc[category] = categories[category].map(opt => opt.value);
        } else if (defaultSelectedOptions[category] && defaultSelectedOptions[category].length > 0) {
            // If defaultSelectAll is false but defaultSelectedOptions has values for the category, use those
            acc[category] = defaultSelectedOptions[category];
        } else {
            // Otherwise, keep it empty
            acc[category] = [];
        }
        return acc;
    }, {});

    const [anchorEl, setAnchorEl] = useState(null);
    const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
    const [tempSelected, setTempSelected] = useState([]);
    const [searchText, setSearchText] = useState(""); // Search state

    // Trigger onInitialLoad once when the component mounts
    useEffect(() => {
        if (onInitialLoad) {
            onInitialLoad(initialSelectedOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Open main menu
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Close all menus
    const handleClose = () => {
        setAnchorEl(null);
        setNestedAnchorEl(null);
        setSelectedCategory(null);
    };

    // Open nested menu (to the right)
    const handleNestedOpen = (event, category) => {
        setNestedAnchorEl(event.currentTarget);
        setSelectedCategory(category);
        setTempSelected(selectedOptions[category] || []); // Load selections for this category
    };

    // Toggle Select All in nested menu
    const handleSelectAll = () => {
        const allValues = filteredOptions.map(opt => opt.value);
        setTempSelected(tempSelected.length === allValues.length ? [] : allValues);
    };

    // Toggle individual selection in nested menu
    const handleCheckboxChange = (value) => {
        setTempSelected(
            tempSelected.includes(value)
                ? tempSelected.filter(item => item !== value)
                : [...tempSelected, value]
        );
    };

    // Apply selection in nested menu
    const handleApply = () => {
        const newSelectedOptions = {
            ...selectedOptions,
            [selectedCategory]: tempSelected
        };
        setSelectedOptions(newSelectedOptions);
        setNestedAnchorEl(null);
        setAnchorEl(null); // Close the main menu
        setSelectedCategory(null);
        onApply(newSelectedOptions);
    };

    // Cancel selection (revert changes)
    const handleCancel = () => {
        // Revert back to the previously selected options for the category
        setTempSelected(selectedOptions[selectedCategory] || []);
        setAnchorEl(null); // Close the main menu
        setNestedAnchorEl(null);
        setSelectedCategory(null);
    };

    // Filter items based on search input
    const filteredOptions = categories[selectedCategory] ?  categories[selectedCategory].filter(option => 
        option.label.toLowerCase().includes(searchText.toLowerCase())
    ) : [] ;

    return (
        <div>
            {/* Dropdown Button */}
            <Tooltip title={title} arrow placement="bottom">
                <Button
                    variant="contained"
                    onClick={handleClick}
                    endIcon={<ArrowDropDownIcon style={{ fontSize: "14px" }} />}
                    style={{
                        backgroundColor: Object.values(selectedOptions).some(arr => arr.length > 0) ? "rgb(34, 41, 180)" : "#013f82",
                        color: "rgb(169, 227, 251)",
                        borderRadius: "20px",
                        border: Object.values(selectedOptions).some(arr => arr.length > 0)
                            ? "1px solid rgb(41, 215, 224)"
                            : "2px solid transparent",
                        boxShadow: Object.values(selectedOptions).some(arr => arr.length > 0)
                            ? "0px 0px 8px rgb(168, 224, 248)"
                            : "none",
                        padding: compact ? "4px 8px" : "4px 16px",
                        textTransform: "none",
                        fontWeight: "normal",
                        minHeight: "30px",
                        minWidth: compact ? "60px" : "120px",
                        fontSize: "14px"
                    }}
                >
                    {icon ? (
                        <img 
                            src={icon} 
                            alt={title || "Icon"} 
                            style={{ 
                                width: "28px", 
                                height: "28px",
                                filter: "brightness(0) saturate(100%) invert(88%) sepia(15%) saturate(1234%) hue-rotate(169deg) brightness(101%) contrast(101%)"
                            }} 
                        />
                    ) : (
                        title
                    )}
                </Button>
            </Tooltip>

            {/* Main Dropdown Menu (Appears Below the Button) */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                keepMounted
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    style: {
                        backgroundColor: "#2b2f3a",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "180px",
                        maxHeight: "250px",
                        overflowY: "auto",
                        padding: "5px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)"
                    }
                }}
            >
                {Object.keys(categories).map(category => (
                    <MenuItem 
                        key={category} 
                        onClick={(event) => handleNestedOpen(event, category)} 
                        style={{ fontSize: "12px", padding: "3px 8px", display: "flex", justifyContent: "space-between" }}
                    >
                        {category}
                        <ChevronRightIcon style={{ fontSize: "12px", color: "#3c82f6" }} />
                    </MenuItem>
                ))}
            </Menu>

            {/* Nested Dropdown Menu (Opens to the Right) */}
            <Menu
                anchorEl={nestedAnchorEl}
                open={Boolean(nestedAnchorEl)}
                onClose={handleCancel}
                keepMounted
                disableAutoFocusItem
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    style: {
                        backgroundColor: "#2b2f3a",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "180px",
                        maxHeight: "250px",
                        overflowY: "auto",
                        padding: "5px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)"
                    }
                }}
            >

                {/* Search Box */}
                <div style={{ padding: "6px 10px", backgroundColor: "#2b2f3a" }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        autoFocus // 💡 Ensures typing stays in the box
                        onKeyDown={(e) => e.stopPropagation()} // 💡 Prevents default menu item selection
                        InputProps={{
                            style: {
                                backgroundColor: "#2b2f3a",
                                color: "#fff",
                                fontSize: "12px",
                                borderRadius: "5px",
                                border: "1px solid #3c82f6"
                            }
                        }}
                        InputLabelProps={{
                            style: { color: "#ffffff80", fontSize: "12px" }
                        }}
                    />
                </div>
                {filteredOptions.length > 0 && (
                    <MenuItem 
                        // onClick={handleSelectAll} 
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: "12px", padding: "2px 8px", minHeight: "20px" }}
                    >
                    <FormControlLabel
                        control={
                            <Checkbox 
                                size="small" 
                                checked={tempSelected.length === filteredOptions?.length}
                                // onClick={(e) => e.stopPropagation()} 
                                onClick={handleSelectAll} 
                                style={{ color: "#3c82f6" }} 
                            />
                        }
                        label={<span style={{ fontSize: "12px" }}>Select All</span>}
                    />
                </MenuItem>)}

                {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                    <MenuItem 
                        key={option.value} 
                        // onClick={() => handleCheckboxChange(option.value)} 
                        onClick={(e) => e.stopPropagation()} 
                        style={{ fontSize: "12px", padding: "2px 8px" }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    size="small" 
                                    checked={tempSelected.includes(option.value)} 
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => handleCheckboxChange(option.value)} 
                                    style={{ color: "#3c82f6" }} 
                                />
                            }
                            label={<span style={{ fontSize: "12px", color: "#ffffff" }}>{option.label}</span>}
                        />
                    </MenuItem>
                )))  : (
                    <MenuItem disabled style={{ fontSize: "12px", color: "#ffffff80", padding: "6px 8px" }}>
                        No results found
                    </MenuItem>)}

                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "#2b2f3a",
                    borderRadius: "0px 0px 8px 8px",
                    position: "sticky",
                    bottom: 0
                }}>
                    <Button 
                        onClick={handleCancel} 
                        style={{ 
                            backgroundColor: "transparent", 
                            color: "rgba(252, 252, 252, 0.5)", 
                            fontSize: "12px", 
                            padding: "4px 10px", 
                            textTransform: "none"
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleApply} 
                        style={{ 
                            backgroundColor: "#3c82f6", 
                            color: "#fff", 
                            fontSize: "12px", 
                            padding: "4px 10px", 
                            textTransform: "none",
                            borderRadius: "5px",
                            fontWeight: "bold"
                        }}
                    >
                        Apply
                    </Button>
                </div>
            </Menu>
        </div>
    );
};

export default DropdownWithNestedMenu;